import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  IconButton,
  Avatar,
  Skeleton,
  Fade,
  Slide,
  Badge,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  useTheme,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
  LinearProgress
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import {
  MonetizationOn as RefundIcon,
  CheckCircle as ProcessedIcon,
  Schedule as PendingIcon,
  Visibility as ViewIcon,
  Event as EventIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Payment as PaymentIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  CloudUpload as UploadIcon,
  AccountBalance as BankIcon,
  CreditCard as CardIcon,
  Phone as PhoneIcon,
  Receipt as ReceiptIcon,
  Close as CloseIcon,
  Security as SecurityIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  History as HistoryIcon // Added for history button
} from '@material-ui/icons';
import moment from 'moment';
import { useNavigate } from 'react-router-dom'; // Added for navigation
import admin_api from '../../../api/admin';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';

const DRAWER_WIDTH = 280;
const COLLAPSE_WIDTH = 102;

const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: '#12192c',
    minHeight: 'calc(100vh - 100px)',
    padding: '20px',
    color: '#ffffff',
    marginTop: '80px',
    marginRight: '24px',
    transition: 'all 0.3s ease',
  },
  paper: {
    backgroundColor: '#1a2235',
    color: '#ffffff',
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    border: '1px solid #2d3748',
  },
  headerCard: {
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: 'white',
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(4),
    boxShadow: '0 10px 30px rgba(34, 197, 94, 0.3)',
  },
  card: {
    backgroundColor: '#1e293b',
    color: '#ffffff',
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    border: '1px solid #2d3748',
    height: '100%',
  },
  successButton: {
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: 'white',
    borderRadius: theme.spacing(3),
    textTransform: 'none',
    fontWeight: 500,
    minWidth: 120,
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
      background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
    },
  },
  // New style for history button
  historyButton: {
    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    color: 'white',
    borderRadius: theme.spacing(3),
    textTransform: 'none',
    fontWeight: 500,
    minWidth: 120,
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
      background: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)',
    },
  },
  tableHead: {
    backgroundColor: '#1e293b',
    color: '#22c55e',
    fontWeight: 'bold',
    borderBottom: '2px solid #2d3748'
  },
  tableRow: {
    '& .MuiTableCell-root': {
      color: '#e2e8f0',
      borderColor: '#2d3748'
    },
    '&:hover': {
      backgroundColor: 'rgba(34, 197, 94, 0.05)'
    }
  },
  avatar: {
    backgroundColor: '#22c55e',
    width: 32,
    height: 32,
    fontSize: '0.8rem'
  },
  dialog: {
    '& .MuiDialog-paper': {
      backgroundColor: '#1a2235',
      color: '#ffffff',
      borderRadius: theme.spacing(2),
      border: '1px solid #2d3748',
      maxWidth: '800px',
      width: '100%'
    },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: theme.spacing(1),
      '& fieldset': {
        borderColor: '#2d3748',
      },
      '&:hover fieldset': {
        borderColor: '#4a5568',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#22c55e',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#a0aec0',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#22c55e',
    },
    '& .MuiOutlinedInput-input': {
      color: '#e2e8f0',
    },
  },
  stepper: {
    backgroundColor: 'transparent',
    '& .MuiStepLabel-root': {
      color: '#e2e8f0',
    },
    '& .MuiStepLabel-active': {
      color: '#22c55e !important',
    },
    '& .MuiStepLabel-completed': {
      color: '#22c55e !important',
    },
    '& .MuiStepIcon-root': {
      color: '#4a5568',
      '&.MuiStepIcon-active': {
        color: '#22c55e',
      },
      '&.MuiStepIcon-completed': {
        color: '#22c55e',
      },
    },
  },
  uploadArea: {
    border: '2px dashed #4a5568',
    borderRadius: theme.spacing(2),
    padding: theme.spacing(4),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: '#22c55e',
      backgroundColor: 'rgba(34, 197, 94, 0.05)',
    },
    '&.dragover': {
      borderColor: '#22c55e',
      backgroundColor: 'rgba(34, 197, 94, 0.1)',
    }
  }
}));

export default function RefundManagement() {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate(); // Added for navigation
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // Enhanced refund processing states
  const [openRefundDialog, setOpenRefundDialog] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  
  // Refund form data
  const [refundData, setRefundData] = useState({
    refundMethod: '',
    recipientName: '',
    accountNumber: '',
    bankName: '',
    mobileNumber: '',
    refundAmount: '',
    referenceNumber: '',
    notes: '',
    screenshot: null
  });

  const { isCollapse } = useCollapseDrawer();

  const containerStyle = {
    marginLeft: isCollapse ? `${COLLAPSE_WIDTH + 24}px` : `${DRAWER_WIDTH + 24}px`,
  };

  if (window.innerWidth <= 1024) {
    containerStyle.marginLeft = '24px';
  }

  // Refund processing steps
  const refundSteps = [
    'Verify Refund Details',
    'Select Refund Method',
    'Enter Recipient Information',
    'Process Refund',
    'Upload Proof & Complete'
  ];

  // Use useCallback to prevent infinite re-renders
  const fetchRefunds = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await admin_api.get_pending_refunds(page + 1, rowsPerPage);
      if (response && response.data && response.data.success) {
        const refundsData = response.data.data || [];
        setRefunds(refundsData);
        setTotalCount(response.data.pagination?.totalRecords || refundsData.length);
      } else {
        console.warn('[RefundManagement] API returned unsuccessful response:', response);
        setError(response?.data?.message || 'Failed to fetch refunds');
        setRefunds([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('[RefundManagement] Error fetching refunds:', error);
      setError('Failed to fetch refunds. Please try again.');
      setRefunds([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchRefunds();
  }, [fetchRefunds]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setError(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  const handleStartRefund = (refund) => {
    setSelectedRefund(refund);
    setRefundData({
      refundMethod: '',
      recipientName: refund.user_name || '',
      accountNumber: '',
      bankName: '',
      mobileNumber: refund.user_phone || '',
      refundAmount: refund.refund_amount || '',
      referenceNumber: '',
      notes: '',
      screenshot: null
    });
    setActiveStep(0);
    setOpenRefundDialog(true);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('File size must be less than 5MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file');
        return;
      }
      
      setRefundData(prev => ({ ...prev, screenshot: file }));
    }
  };

  // New function to handle navigation to history page
  const handleViewHistory = () => {
    navigate('/admin/refunds/history');
  };

  const handleCompleteRefund = async () => {    
    const refundId = selectedRefund.refund_id || selectedRefund.id || selectedRefund.cancellation_id;
    if (!refundId) {
        setError('Unable to identify refund record. Please refresh and try again.');
        return;
    }

    setProcessing(true);
    try {
      // Since we don't have file upload in the current API, we'll send JSON data
      const response = await admin_api.update_refund_status(refundId, {
        refundStatus: 'completed',
        adminNotes: `Refund processed via ${refundData.refundMethod}. Recipient: ${refundData.recipientName}. Account: ${refundData.accountNumber}. Reference: ${refundData.referenceNumber}. Notes: ${refundData.notes}`
      });

      if (response && response.data && response.data.success) {
        setSuccessMessage(`Refund of ₱${parseFloat(selectedRefund.refund_amount).toLocaleString()} has been processed successfully!`);
        setOpenRefundDialog(false);
        setSelectedRefund(null);
        setActiveStep(0);
        setRefundData({
          refundMethod: '',
          recipientName: '',
          accountNumber: '',
          bankName: '',
          mobileNumber: '',
          refundAmount: '',
          referenceNumber: '',
          notes: '',
          screenshot: null
        });
        fetchRefunds();
      } else {
        setError(response?.data?.message || 'Failed to process refund');
      }
    } catch (error) {
      console.error('[RefundManagement] Error processing refund:', error);
      setError('Failed to process refund. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate summary statistics
  const summaryStats = {
    pendingRefunds: refunds.filter(r => r.refund_status === 'pending').length,
    totalPendingAmount: refunds
      .filter(r => r.refund_status === 'pending')
      .reduce((sum, r) => sum + parseFloat(r.refund_amount || 0), 0),
    processedToday: refunds.filter(r => {
      const processedDate = r.refund_processed_at;
      if (!processedDate) return false;
      return moment(processedDate).isSame(moment(), 'day');
    }).length
  };

  const formatDateToPH = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).utcOffset('+08:00').format('MMM D, YYYY');
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom style={{ color: '#22c55e' }}>
              Verify Refund Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper style={{ padding: 16, backgroundColor: '#2d3748', marginBottom: 16 }}>
                  <Typography variant="subtitle1" gutterBottom style={{ color: '#06b6d4' }}>
                    Customer Information
                  </Typography>
                  <Typography><strong>Name:</strong> {selectedRefund?.user_name}</Typography>
                  <Typography><strong>Email:</strong> {selectedRefund?.user_email}</Typography>
                  <Typography><strong>Phone:</strong> {selectedRefund?.user_phone || 'N/A'}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper style={{ padding: 16, backgroundColor: '#2d3748', marginBottom: 16 }}>
                  <Typography variant="subtitle1" gutterBottom style={{ color: '#f59e0b' }}>
                    Refund Information
                  </Typography>
                  <Typography><strong>Booking:</strong> {selectedRefund?.booking_id}</Typography>
                  <Typography><strong>Amount:</strong> ₱{parseFloat(selectedRefund?.refund_amount || 0).toLocaleString()}</Typography>
                  <Typography><strong>Original Payment:</strong> {selectedRefund?.payment_method}</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom style={{ color: '#22c55e' }}>
              Select Refund Method
            </Typography>
            <FormControl fullWidth className={classes.textField} style={{ marginBottom: 16 }}>
              <InputLabel>Refund Method</InputLabel>
              <Select
                value={refundData.refundMethod}
                onChange={(e) => setRefundData(prev => ({ ...prev, refundMethod: e.target.value }))}
                label="Refund Method"
              >
                <MenuItem value="gcash">
                  <Box display="flex" alignItems="center">
                    <PhoneIcon style={{ marginRight: 8, color: '#06b6d4' }} />
                    GCash
                  </Box>
                </MenuItem>
                <MenuItem value="maya">
                  <Box display="flex" alignItems="center">
                    <PhoneIcon style={{ marginRight: 8, color: '#06b6d4' }} />
                    Maya (PayMaya)
                  </Box>
                </MenuItem>
                <MenuItem value="bank_transfer">
                  <Box display="flex" alignItems="center">
                    <BankIcon style={{ marginRight: 8, color: '#06b6d4' }} />
                    Bank Transfer
                  </Box>
                </MenuItem>
                <MenuItem value="cash">
                  <Box display="flex" alignItems="center">
                    <MoneyIcon style={{ marginRight: 8, color: '#06b6d4' }} />
                    Cash Refund
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom style={{ color: '#22c55e' }}>
              Enter Recipient Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Recipient Name"
                  value={refundData.recipientName}
                  onChange={(e) => setRefundData(prev => ({ ...prev, recipientName: e.target.value }))}
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Refund Amount"
                  type="number"
                  value={refundData.refundAmount}
                  onChange={(e) => setRefundData(prev => ({ ...prev, refundAmount: e.target.value }))}
                  className={classes.textField}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₱</InputAdornment>,
                  }}
                  required
                />
              </Grid>
              
              {(refundData.refundMethod === 'gcash' || refundData.refundMethod === 'maya') && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Mobile Number"
                    value={refundData.mobileNumber}
                    onChange={(e) => setRefundData(prev => ({ ...prev, mobileNumber: e.target.value }))}
                    className={classes.textField}
                    placeholder="09XXXXXXXXX"
                    required
                  />
                </Grid>
              )}
              
              {refundData.refundMethod === 'bank_transfer' && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Bank Name"
                      value={refundData.bankName}
                      onChange={(e) => setRefundData(prev => ({ ...prev, bankName: e.target.value }))}
                      className={classes.textField}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Account Number"
                      value={refundData.accountNumber}
                      onChange={(e) => setRefundData(prev => ({ ...prev, accountNumber: e.target.value }))}
                      className={classes.textField}
                      required
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom style={{ color: '#22c55e' }}>
              Process Refund
            </Typography>
            <Alert severity="info" style={{ backgroundColor: 'rgba(6, 182, 212, 0.1)', color: '#06b6d4', marginBottom: 16 }}>
              Please process the refund using your preferred method and enter the reference number below.
            </Alert>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Reference Number"
                  value={refundData.referenceNumber}
                  onChange={(e) => setRefundData(prev => ({ ...prev, referenceNumber: e.target.value }))}
                  className={classes.textField}
                  placeholder="Enter transaction reference number"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" style={{ color: '#a0aec0', marginBottom: 8 }}>
                  Refund Summary:
                </Typography>
                <Typography><strong>Method:</strong> {refundData.refundMethod.toUpperCase()}</Typography>
                <Typography><strong>Recipient:</strong> {refundData.recipientName}</Typography>
                <Typography><strong>Amount:</strong> ₱{parseFloat(refundData.refundAmount).toLocaleString()}</Typography>
                {refundData.mobileNumber && <Typography><strong>Mobile:</strong> {refundData.mobileNumber}</Typography>}
                {refundData.accountNumber && <Typography><strong>Account:</strong> {refundData.accountNumber}</Typography>}
              </Grid>
            </Grid>
          </Box>
        );

      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom style={{ color: '#22c55e' }}>
              Upload Proof & Complete
            </Typography>
            
            <Box className={classes.uploadArea} onClick={() => document.getElementById('screenshot-upload').click()}>
              <input
                id="screenshot-upload"
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              <UploadIcon style={{ fontSize: 48, color: '#4a5568', marginBottom: 16 }} />
              <Typography variant="h6" style={{ color: '#e2e8f0', marginBottom: 8 }}>
                {refundData.screenshot ? 'Screenshot Uploaded' : 'Upload Refund Screenshot'}
              </Typography>
              <Typography variant="body2" style={{ color: '#a0aec0' }}>
                {refundData.screenshot 
                  ? `File: ${refundData.screenshot.name}` 
                  : 'Click to upload screenshot of refund transaction (Max 5MB)'}
              </Typography>
            </Box>
            
            <TextField
              fullWidth
              label="Additional Notes (Optional)"
              multiline
              rows={3}
              value={refundData.notes}
              onChange={(e) => setRefundData(prev => ({ ...prev, notes: e.target.value }))}
              className={classes.textField}
              style={{ marginTop: 16 }}
              placeholder="Any additional notes about this refund..."
            />
            
            <Alert severity="success" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', marginTop: 16 }}>
              Ready to complete refund! Please review all information before proceeding.
            </Alert>
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  const isStepValid = (step) => {
    switch (step) {
      case 0:
        return true; // Verification step is always valid
      case 1:
        return refundData.refundMethod !== '';
      case 2:
        if (refundData.refundMethod === 'gcash' || refundData.refundMethod === 'maya') {
          return refundData.recipientName && refundData.mobileNumber && refundData.refundAmount;
        } else if (refundData.refundMethod === 'bank_transfer') {
          return refundData.recipientName && refundData.bankName && refundData.accountNumber && refundData.refundAmount;
        } else if (refundData.refundMethod === 'cash') {
          return refundData.recipientName && refundData.refundAmount;
        }
        return false;
      case 3:
        return refundData.referenceNumber !== '';
      case 4:
        return true; // Screenshot is optional, so this step is always valid
      default:
        return false;
    }
  };

  return (
    <Box className={classes.container} style={containerStyle}>
      {/* Success Message */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={8000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" style={{ backgroundColor: '#22c55e', minWidth: '400px' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Message */}
      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" style={{ backgroundColor: '#ef4444' }}>
          {error}
        </Alert>
      </Snackbar>

      <Fade in timeout={800}>
        <Box>
          {/* Header */}
          <Card className={classes.headerCard}>
            <CardContent style={{ padding: theme.spacing(4) }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 8 }}>
                    Refund Management
                  </Typography>
                  <Typography variant="h6" style={{ opacity: 0.9 }}>
                    Process refunds with step-by-step verification
                  </Typography>
                </Box>
                {/* Updated header buttons section with History button */}
                <Box style={{ display: 'flex', gap: 16 }}>
                  <Button
                    variant="contained"
                    className={classes.historyButton}
                    startIcon={<HistoryIcon />}
                    onClick={handleViewHistory}
                  >
                    View History
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={fetchRefunds}
                    disabled={loading}
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      borderRadius: theme.spacing(3),
                    }}
                  >
                    Refresh
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <Grid container spacing={3} style={{ marginBottom: theme.spacing(4) }}>
            <Grid item xs={12} md={4}>
              <Slide direction="up" in timeout={1000}>
                <Card className={classes.card}>
                  <CardContent style={{ textAlign: 'center', padding: theme.spacing(3) }}>
                    <PendingIcon style={{ fontSize: 40, color: '#f59e0b', marginBottom: 16 }} />
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Pending Refunds
                    </Typography>
                    <Typography variant="h4" style={{ color: '#f59e0b', fontWeight: 'bold' }}>
                      {summaryStats.pendingRefunds}
                    </Typography>
                    <Typography style={{ color: '#a0aec0', marginTop: 8 }}>
                      Awaiting processing
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Slide direction="up" in timeout={1200}>
                <Card className={classes.card}>
                  <CardContent style={{ textAlign: 'center', padding: theme.spacing(3) }}>
                    <Badge badgeContent={summaryStats.pendingRefunds} color="error" max={99}>
                      <MoneyIcon style={{ fontSize: 40, color: '#22c55e', marginBottom: 16 }} />
                    </Badge>
                    <Typography variant="h4" style={{ color: '#22c55e', fontWeight: 'bold' }}>
                      ₱{summaryStats.totalPendingAmount.toLocaleString()}
                    </Typography>
                    <Typography style={{ color: '#a0aec0', marginTop: 8 }}>
                      Total to be refunded
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Slide direction="up" in timeout={1400}>
                <Card className={classes.card}>
                  <CardContent style={{ textAlign: 'center', padding: theme.spacing(3) }}>
                    <ProcessedIcon style={{ fontSize: 40, color: '#06b6d4', marginBottom: 16 }} />
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Processed Today
                    </Typography>
                    <Typography variant="h4" style={{ color: '#06b6d4', fontWeight: 'bold' }}>
                      {summaryStats.processedToday}
                    </Typography>
                    <Typography style={{ color: '#a0aec0', marginTop: 8 }}>
                      Refunds completed
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
          </Grid>

          {/* Refunds Table */}
          <Slide direction="up" in timeout={1600}>
            <TableContainer component={Paper} className={classes.paper}>
              {loading ? (
                <Box style={{ padding: theme.spacing(4) }}>
                  {[...Array(5)].map((_, index) => (
                    <Box key={index} style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                      <Skeleton variant="circle" width={40} height={40} style={{ backgroundColor: '#2d3748' }} />
                      <Skeleton variant="text" width="30%" height={40} style={{ backgroundColor: '#2d3748' }} />
                      <Skeleton variant="text" width="40%" height={40} style={{ backgroundColor: '#2d3748' }} />
                      <Skeleton variant="text" width="20%" height={40} style={{ backgroundColor: '#2d3748' }} />
                    </Box>
                  ))}
                </Box>
              ) : refunds.length === 0 ? (
                <Box style={{ padding: theme.spacing(6), textAlign: 'center' }}>
                  <AssignmentIcon style={{ fontSize: 60, color: '#4a5568', marginBottom: 16 }} />
                  <Typography variant="h6" style={{ color: '#e2e8f0', marginBottom: 8 }}>
                    No pending refunds
                  </Typography>
                  <Typography variant="body2" style={{ color: '#a0aec0', marginBottom: 24 }}>
                    All refunds have been processed or no refunds are pending.
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchRefunds}
                    style={{
                      color: '#e2e8f0',
                      borderColor: '#4a5568',
                    }}
                  >
                    Refresh List
                  </Button>
                </Box>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHead}>Customer</TableCell>
                      <TableCell className={classes.tableHead}>Booking</TableCell>
                      <TableCell className={classes.tableHead}>Cancellation</TableCell>
                      <TableCell className={classes.tableHead}>Payment Info</TableCell>
                      <TableCell className={classes.tableHead}>Refund Amount</TableCell>
                      <TableCell className={classes.tableHead}>Status</TableCell>
                      <TableCell className={classes.tableHead} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {refunds.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((refund, index) => (
                      <Fade in timeout={300 + index * 100} key={refund.id || `refund-${index}`}>
                        <TableRow className={classes.tableRow}>
                          <TableCell>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                              <Avatar className={classes.avatar}>
                                {(refund.user_name || 'U').charAt(0).toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                                  {refund.user_name || 'Unknown User'}
                                </Typography>
                                <Typography variant="caption" style={{ color: '#a0aec0' }}>
                                  {refund.user_email || 'No email'}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                                {refund.booking_id || 'N/A'}
                              </Typography>
                              <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <EventIcon style={{ fontSize: 14, color: '#22c55e' }} />
                                <Typography variant="caption" style={{ color: '#a0aec0' }}>
                                  {formatDateToPH(refund.reservation_date)}
                                </Typography>
                              </Box>
                              <Typography variant="caption" style={{ color: '#a0aec0' }}>
                                {refund.court_name || 'Unknown Court'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Chip 
                                label={refund.auto_processed ? 'Auto-Cancelled' : 'Manual'} 
                                size="small"
                                style={{
                                  backgroundColor: refund.auto_processed ? 'rgba(34, 197, 94, 0.1)' : 'rgba(6, 182, 212, 0.1)',
                                  color: refund.auto_processed ? '#22c55e' : '#06b6d4',
                                  marginBottom: 4
                                }}
                              />
                              <Typography variant="caption" style={{ color: '#a0aec0', display: 'block' }}>
                                {moment(refund.requested_at).fromNow()}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                                {refund.payment_method?.toUpperCase() || 'N/A'}
                              </Typography>
                              <Typography variant="caption" style={{ color: '#a0aec0', display: 'block' }}>
                                Ref: {refund.reference_number || 'N/A'}
                              </Typography>
                              <Typography variant="caption" style={{ color: '#a0aec0' }}>
                                Paid: ₱{parseFloat(refund.paid_amount || 0).toLocaleString()}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="h6" style={{ color: '#22c55e', fontWeight: 'bold' }}>
                              ₱{parseFloat(refund.refund_amount || 0).toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={refund.refund_status === 'pending' ? 'PENDING' : 'PROCESSED'} 
                              size="small"
                              style={{
                                backgroundColor: refund.refund_status === 'pending' 
                                  ? 'rgba(245, 158, 11, 0.1)' 
                                  : 'rgba(34, 197, 94, 0.1)',
                                color: refund.refund_status === 'pending' 
                                  ? '#f59e0b' 
                                  : '#22c55e',
                                border: `1px solid ${refund.refund_status === 'pending' 
                                  ? '#f59e0b' 
                                  : '#22c55e'}`
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {refund.refund_status === 'pending' ? (
                              <Tooltip title="Start Refund Process">
                                <Button
                                  variant="contained"
                                  size="small"
                                  className={classes.successButton}
                                  startIcon={<MoneyIcon />}
                                  onClick={() => handleStartRefund(refund)}
                                  style={{ minWidth: 'auto', padding: '8px 16px' }}
                                >
                                  Process
                                </Button>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Already Processed">
                                <IconButton disabled>
                                  <ProcessedIcon style={{ color: '#4a5568' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      </Fade>
                    ))}
                  </TableBody>
                </Table>
              )}
              
              {refunds.length > 0 && (
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={totalCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                  style={{
                    color: '#e2e8f0',
                    borderTop: '1px solid #2d3748',
                  }}
                />
              )}
            </TableContainer>
          </Slide>
        </Box>
      </Fade>

      {/* Enhanced Refund Processing Dialog */}
      <Dialog
        open={openRefundDialog}
        onClose={() => !processing && setOpenRefundDialog(false)}
        maxWidth="md"
        fullWidth
        classes={{ paper: classes.dialog }}
      >
        <DialogTitle style={{ 
          borderBottom: '1px solid #2d3748',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          color: '#22c55e'
        }}>
          <RefundIcon />
          Process Refund - Step {activeStep + 1} of {refundSteps.length}
          {selectedRefund && (
            <Typography variant="body2" style={{ marginLeft: 'auto', color: '#a0aec0' }}>
              Booking: {selectedRefund.booking_id}
            </Typography>
          )}
        </DialogTitle>
        
        <DialogContent style={{ padding: theme.spacing(3) }}>
          <Stepper activeStep={activeStep} orientation="vertical" className={classes.stepper}>
            {refundSteps.map((label, index) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
                <StepContent>
                  {getStepContent(index)}
                  <Box sx={{ mb: 2 }}>
                    <div>
                      <Button
                        variant="contained"
                        onClick={index === refundSteps.length - 1 ? handleCompleteRefund : handleNext}
                        disabled={!isStepValid(index) || processing}
                        className={classes.successButton}
                        style={{ marginTop: 16, marginRight: 8 }}
                        startIcon={processing ? <CircularProgress size={20} color="inherit" /> : null}
                      >
                        {processing 
                          ? 'Processing...' 
                          : index === refundSteps.length - 1 
                            ? 'Complete Refund' 
                            : 'Continue'}
                      </Button>
                      <Button
                        disabled={index === 0 || processing}
                        onClick={handleBack}
                        style={{ marginTop: 16, color: '#a0aec0' }}
                      >
                        Back
                      </Button>
                    </div>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </DialogContent>
        
        <DialogActions style={{ padding: theme.spacing(3), borderTop: '1px solid #2d3748', gap: 16 }}>
          <Button 
            onClick={() => setOpenRefundDialog(false)} 
            disabled={processing}
            style={{ color: '#a0aec0' }}
          >
            Cancel Process
          </Button>
          <Typography variant="body2" style={{ color: '#a0aec0', marginLeft: 'auto' }}>
            Refund Amount: ₱{parseFloat(selectedRefund?.refund_amount || 0).toLocaleString()}
          </Typography>
        </DialogActions>
      </Dialog>
    </Box>
  );
}